import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { MenuProvider } from './context/MenuContext';
import { ThemeProvider } from './context/ThemeContext';
import ProtectedRoute from './components/common/ProtectedRoute';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import DashboardPage from './pages/DashboardPage';
import SolicitudesPage from "./pages/SolicitudesPage";
import ProgramacionesMantenimientoPage from "./pages/ProgramacionesMantenimientoPage";
import ObservacionesPage from "./pages/ObservacionesPage";
import WorkOrdersPage from './pages/WorkOrdersPage';
import ActivosPage from "./pages/ActivosPage";
import CategoriasPage from "./pages/CategoriasPage";
import UbicacionesPage from "./pages/UbicacionesPage";
import UsersPage from "./pages/UsersPage";
import SessionPage from "./pages/SessionPage";
import ConsumoServicioPage from "./pages/ConsumoServicioPage";
import FinanzasPage from "./pages/FinanzasPage";
import PlanMantenimientoPage from "./pages/PlanMantenimientoPage";
import RRHHDashboardPage from "./pages/RRHHDashboardPage";
import RRHHCatalogoPage from "./pages/RRHHCatalogoPage";
import RRHHMovimientosPage from "./pages/RRHHMovimientosPage";
import RRHHEmpleadosPage from "./pages/RRHHEmpleadosPage";
import RRHHCaducidadesPage from "./pages/RRHHCaducidadesPage";
import RRHHSolicitudesPage from "./pages/RRHHSolicitudesPage";
import SolicitudColaboradorPage from "./pages/SolicitudColaboradorPage";
import InspeccionesPage from "./pages/InspeccionesPage";
import HistorialInspeccionesPage from "./pages/HistorialInspeccionesPage";
import SSTPage from "./pages/SSTPage";
import HistorialSSTPage from "./pages/HistorialSSTPage";
import CCTVPage from "./pages/CCTVPage";
import HistorialCCTVPage from "./pages/HistorialCCTVPage";
import CIGeneralesPage from "./pages/CIGeneralesPage";
import HistorialCIGeneralesPage from "./pages/HistorialCIGeneralesPage";
import InspeccionModuloPage from "./pages/InspeccionModuloPage";
import HistorialInspeccionModuloPage from "./pages/HistorialInspeccionModuloPage";
import LicenciasITSEPage from "./pages/LicenciasITSEPage";
import ResumenLicenciasPage from "./pages/ResumenLicenciasPage";
import AjustesPage from "./pages/AjustesPage";
import ComingSoonPage from "./pages/ComingSoonPage";
import SostenibilidadPage from "./pages/SostenibilidadPage";

// Styles
import './styles/globals.css';

function App() {
    return (
        <ThemeProvider>
          <Router>
            <AuthProvider>
              <MenuProvider>
                <div className="App">
                    <Toaster
                        position="top-right"
                        toastOptions={{
                            duration: 4000,
                            style: {
                                background: '#363636',
                                color: '#fff',
                            },
                            success: {
                                duration: 3000,
                                style: {
                                    background: '#10b981',
                                    color: '#fff',
                                },
                            },
                            error: {
                                duration: 4000,
                                style: {
                                    background: '#ef4444',
                                    color: '#fff',
                                },
                            },
                        }}
                    />

                    <Routes>
                        {/* Ruta de inicio (pública/privada) */}
                        <Route path="/" element={<LoginPage />} />

                        {/* Rutas públicas de autenticación */}
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/home" element={<HomePage />} />
                        <Route path="/register" element={<RegisterPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                        <Route path="/delete-account" element={<DeleteAccountPage />} />

                        {/* Formulario público de requerimientos para colaboradores (sin login) */}
                        <Route path="/solicitud-rrhh" element={<SolicitudColaboradorPage />} />

                        {/* Ruta de sesión activa - Solo para usuarios XD (y accesible a todos) */}
                        <Route
                            path="/my-session"
                            element={
                                <ProtectedRoute xdAllowed={true}>
                                    <SessionPage />
                                </ProtectedRoute>
                            }
                        />

                        {/* Rutas protegidas - Acceso para todos los usuarios autenticados */}
                        <Route
                            path="/dashboard"
                            element={
                                <ProtectedRoute>
                                    <DashboardPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/job-application"
                            element={
                                <ProtectedRoute menuKey="solicitudes">
                                    <SolicitudesPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/programaciones-mantenimiento"
                            element={
                                <ProtectedRoute menuKey="programaciones_mantenimiento">
                                    <ProgramacionesMantenimientoPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/observaciones"
                            element={
                                <ProtectedRoute menuKey="observaciones">
                                    <ObservacionesPage />
                                </ProtectedRoute>
                            }
                        />

                        {/* Rutas protegidas - Solo administradores */}
                        <Route
                            path="/activos"
                            element={
                                <ProtectedRoute xdAllowed={true} lectorAllowed={true}>
                                    <ActivosPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/categorias"
                            element={
                                <ProtectedRoute adminOnly={true}>
                                    <CategoriasPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/ubicaciones"
                            element={
                                <ProtectedRoute adminOnly={true}>
                                    <UbicacionesPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/work-orders"
                            element={
                                <ProtectedRoute adminOnly={true}>
                                    <WorkOrdersPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/plan-mantenimiento"
                            element={
                                <ProtectedRoute menuKey="plan_mantenimiento">
                                    <PlanMantenimientoPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/rrhh"
                            element={
                                <ProtectedRoute menuKey="rrhh_dashboard">
                                    <RRHHDashboardPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/rrhh/catalogo"
                            element={
                                <ProtectedRoute menuKey="rrhh_catalogo">
                                    <RRHHCatalogoPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/rrhh/movimientos"
                            element={
                                <ProtectedRoute menuKey="rrhh_movimientos">
                                    <RRHHMovimientosPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/rrhh/empleados"
                            element={
                                <ProtectedRoute menuKey="rrhh_empleados">
                                    <RRHHEmpleadosPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/rrhh/caducidades"
                            element={
                                <ProtectedRoute menuKey="rrhh_caducidades">
                                    <RRHHCaducidadesPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/rrhh/solicitudes"
                            element={
                                <ProtectedRoute menuKey="rrhh_solicitudes">
                                    <RRHHSolicitudesPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/users"
                            element={
                                <ProtectedRoute adminOnly={true}>
                                    <UsersPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/consumo-servicio"
                            element={
                                <ProtectedRoute menuKey="consumo_servicio">
                                    <ConsumoServicioPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/inspecciones"
                            element={
                                <ProtectedRoute menuKey="inspecciones">
                                    <InspeccionesPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/inspecciones/historial"
                            element={
                                <ProtectedRoute menuKey="inspecciones">
                                    <HistorialInspeccionesPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/inspeccion-sst"
                            element={
                                <ProtectedRoute menuKey="inspeccion_sst">
                                    <SSTPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/inspeccion-sst/historial"
                            element={
                                <ProtectedRoute menuKey="inspeccion_sst">
                                    <HistorialSSTPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/inspeccion-cctv"
                            element={
                                <ProtectedRoute menuKey="inspeccion_cctv">
                                    <CCTVPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/inspeccion-cctv/historial"
                            element={
                                <ProtectedRoute menuKey="inspeccion_cctv">
                                    <HistorialCCTVPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/inspeccion-mtto/faja-transportadora"
                            element={
                                <ProtectedRoute menuKey="inspeccion_mtto_faja">
                                    <InspeccionModuloPage modulo="mtto_faja_transportadora" titulo="Inspección MTTO — Faja Transportadora" />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/inspeccion-mtto/te"
                            element={
                                <ProtectedRoute menuKey="inspeccion_mtto_te">
                                    <InspeccionModuloPage modulo="mtto_te" titulo="Inspección MTTO — T.E." />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/inspeccion-mtto/dti-aci"
                            element={
                                <ProtectedRoute menuKey="inspeccion_mtto_dti_aci">
                                    <InspeccionModuloPage modulo="mtto_dti_aci" titulo="Inspección MTTO — DTI y ACI" />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/inspeccion-mtto/equipos-movimiento"
                            element={
                                <ProtectedRoute menuKey="inspeccion_mtto_equipos_movimiento">
                                    <InspeccionModuloPage modulo="mtto_equipos_movimiento" titulo="Inspección MTTO — Equipos de Movimiento" />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/inspeccion-ti"
                            element={
                                <ProtectedRoute menuKey="inspeccion_ti">
                                    <InspeccionModuloPage modulo="ti" titulo="Inspecciones TI" />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/inspeccion-operaciones"
                            element={
                                <ProtectedRoute menuKey="inspeccion_operaciones">
                                    <InspeccionModuloPage
                                        modulo="operaciones"
                                        titulo="Inspecciones Operaciones"
                                        rutaHistorial="/inspeccion-operaciones/historial"
                                    />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/inspeccion-operaciones/historial"
                            element={
                                <ProtectedRoute menuKey="inspeccion_operaciones">
                                    <HistorialInspeccionModuloPage
                                        modulo="operaciones"
                                        titulo="Inspecciones Operaciones"
                                        rutaBase="/inspeccion-operaciones"
                                    />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/inspeccion-ci-generales"
                            element={
                                <ProtectedRoute menuKey="inspeccion_ci_generales">
                                    <CIGeneralesPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/inspeccion-ci-generales/historial"
                            element={
                                <ProtectedRoute menuKey="inspeccion_ci_generales">
                                    <HistorialCIGeneralesPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/inspeccion-ci-otras"
                            element={
                                <ProtectedRoute menuKey="inspeccion_ci_otras">
                                    <InspeccionModuloPage modulo="ci_otras" titulo="Otras Inspecciones (C.I.)" />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/ajustes"
                            element={
                                <ProtectedRoute menuKey="ajustes">
                                    <AjustesPage />
                                </ProtectedRoute>
                            }
                        />

                        {/* Módulos en proceso - Solo administradores */}
                        <Route
                            path="/repuestos"
                            element={
                                <ProtectedRoute adminOnly={true}>
                                    <ComingSoonPage
                                        titulo="Repuestos e Insumos"
                                        descripcion="Control de stock de repuestos e insumos."
                                        icono="Boxes"
                                    />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/finanzas"
                            element={
                                <ProtectedRoute menuKey="finanzas">
                                    <FinanzasPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/gestion-documental"
                            element={<Navigate to="/gestion-documental/licencias-itse" replace />}
                        />

                        <Route
                            path="/gestion-documental/licencias-itse"
                            element={
                                <ProtectedRoute menuKey="documental_licencias">
                                    <LicenciasITSEPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/gestion-documental/licencias-itse/resumen"
                            element={
                                <ProtectedRoute menuKey="documental_licencias">
                                    <ResumenLicenciasPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/proveedores"
                            element={
                                <ProtectedRoute adminOnly={true}>
                                    <ComingSoonPage
                                        titulo="Proveedores"
                                        descripcion="Directorio y gestión de proveedores."
                                        icono="Truck"
                                    />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/sostenibilidad"
                            element={
                                <ProtectedRoute menuKey="sostenibilidad">
                                    <SostenibilidadPage />
                                </ProtectedRoute>
                            }
                        />

                        {/* Redirección para rutas no encontradas */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </div>
              </MenuProvider>
            </AuthProvider>
          </Router>
        </ThemeProvider>
    );
}

export default App;
